import { useState } from "react";
import { MagnifyingGlass, Funnel, SortAscending, X } from "@phosphor-icons/react";

const SessionFilters = ({ filters, onFilterChange }) => {
  const [searchInput, setSearchInput] = useState(filters.search || "");

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    onFilterChange({ ...filters, search: searchInput });
  };

  const handleClearSearch = () => {
    setSearchInput("");
    onFilterChange({ ...filters, search: "" });
  };

  const setFilter = (key, value) => {
    onFilterChange({ ...filters, [key]: value });
  };

  const activeCount = [
    filters.search,
    filters.status !== "all" && filters.status,
    filters.channel !== "all" && filters.channel,
    filters.handoff !== "all" && filters.handoff
  ].filter(Boolean).length;

  const clearAll = () => {
    setSearchInput("");
    onFilterChange({ search: "", status: "all", channel: "all", handoff: "all", sort: "newest" });
  };

  const FilterChip = ({ label, value, current, filterKey }) => (
    <button
      onClick={() => setFilter(filterKey, value)}
      data-testid={`filter-${filterKey}-${value}`}
      className={`px-2.5 py-1 rounded text-xs font-medium transition-all ${
        current === value
          ? "bg-[#E85D24] text-white"
          : "bg-[#1A1A1A] text-[#888] border border-[#2A2A2A] hover:border-[#444] hover:text-white"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="mb-5 space-y-3" data-testid="session-filters">
      {/* Search bar */}
      <form onSubmit={handleSearchSubmit} className="flex gap-2">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by name, email, or phone..."
            data-testid="session-search-input"
            className="w-full pl-9 pr-8 py-2 bg-[#111] border border-[#2A2A2A] rounded-lg text-sm text-white placeholder-[#555] focus:outline-none focus:border-[#E85D24] transition-colors"
          />
          {searchInput && (
            <button
              type="button"
              onClick={handleClearSearch}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#555] hover:text-white"
              data-testid="search-clear-btn"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <button
          type="submit"
          data-testid="session-search-btn"
          className="px-4 py-2 bg-[#E85D24] text-white text-sm font-medium rounded-lg hover:opacity-85 transition-opacity"
        >
          Search
        </button>
      </form>

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-4">
        {/* Status */}
        <div className="flex items-center gap-1.5">
          <Funnel size={14} className="text-[#555]" />
          <span className="text-xs text-[#555] mr-1">Status</span>
          {[
            { label: "All", value: "all" },
            { label: "Hot", value: "hot" },
            { label: "Warm", value: "warm" },
            { label: "Cold", value: "cold" },
            { label: "Converted", value: "converted" }
          ].map(opt => (
            <FilterChip key={opt.value} label={opt.label} value={opt.value} current={filters.status} filterKey="status" />
          ))}
        </div>

        <div className="w-px h-5 bg-[#2A2A2A]" />

        {/* Channel */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-[#555] mr-1">Channel</span>
          {[
            { label: "All", value: "all" },
            { label: "Website", value: "website" },
            { label: "WhatsApp", value: "whatsapp" }
          ].map(opt => (
            <FilterChip key={opt.value} label={opt.label} value={opt.value} current={filters.channel} filterKey="channel" />
          ))}
        </div>

        <div className="w-px h-5 bg-[#2A2A2A]" />

        {/* Handoff */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-[#555] mr-1">Handoff</span>
          {[
            { label: "All", value: "all" },
            { label: "Sent", value: "sent" },
            { label: "Pending", value: "pending" }
          ].map(opt => (
            <FilterChip key={opt.value} label={opt.label} value={opt.value} current={filters.handoff} filterKey="handoff" />
          ))}
        </div>

        <div className="w-px h-5 bg-[#2A2A2A]" />

        {/* Sort */}
        <div className="flex items-center gap-1.5">
          <SortAscending size={14} className="text-[#555]" />
          <select
            value={filters.sort}
            onChange={(e) => setFilter("sort", e.target.value)}
            data-testid="session-sort-select"
            className="bg-[#1A1A1A] border border-[#2A2A2A] text-xs text-white rounded px-2 py-1 focus:outline-none focus:border-[#E85D24]"
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="score_desc">Score (high-low)</option>
            <option value="score_asc">Score (low-high)</option>
          </select>
        </div>

        {/* Active filters indicator + clear */}
        {activeCount > 0 && (
          <>
            <div className="w-px h-5 bg-[#2A2A2A]" />
            <button
              onClick={clearAll}
              data-testid="clear-all-filters-btn"
              className="text-xs text-[#E85D24] hover:underline"
            >
              Clear all ({activeCount})
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default SessionFilters;
